// ⚠ CONTRAST, 2026-09-14. Real copy in this file was painted in
// `MK.smoke` (#59536a) and `MK.ash` (#8b8496) directly on `MK.obsidian`
// — about 2.7:1, well under the readable floor, on instructional text
// and shelf prose that people are meant to actually read.
// ⛑ `markData.js` already solved this: `ashP` / `smokeP` / `verdP`,
// with the note "THE NUMBERS ARE OUTPUTS, NOT TASTE". `Mark.js` uses
// them correctly and has zero raw `MK.ash`/`MK.smoke`; this file and
// the Marketplace were simply the two consumers the fix never reached.
// ⚑ Swapped ONLY where the token paints TEXT — borders and fills keep
// the originals, which is what the plated-vs-unplated note intends.
// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — MarkEditor.js   ·   THE MARK, owner's side
// ────────────────────────────────────────────────────────────────────
// The only screen in the Empire that writes to profiles/{uid}/mark, and
// it writes nothing else. Tagline, status, accent, pinned deeds, links,
// storefront listings — all self-authored decoration, none of it earned,
// none of it worth cheating for. That is the whole reason it is safe to
// let a client write it directly with no server in the middle.
//
// WHAT THIS FILE CANNOT DO, BY CONSTRUCTION:
//   • move a Divine Crest        • grant a deed        • unlock a cosmetic
//   • touch another player's Mark (markData.js refuses on auth mismatch)
//   • create a card, a record, or a session
// If any of those ever needs doing, it does not get added here — it goes
// through the granting helper that owns it, or through a server.
//
// PINNING IS NOT CLAIMING. You may pin only deeds you actually hold: the
// picker lists earned ids only, AND the renderer re-checks against the
// live earned map, so a pin written by hand into the database displays
// as nothing. Two independent checks, neither trusting the other.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   <MarkEditor uid={myUid} onClose={close} />
// ════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from "react";
import {
  useMark, accentOf, MARK_ACCENTS, CAPS, safeUrl,
  saveMarkBasics, savePinned, saveLinks,
  upsertListing, removeListing, setStoreOpen, fmtNum,
  ilkOf, ilkTagError, saveIlkTag, savePartner, uidForCode, partnerOf,
  ILK_SYMBOLS, ILK_INFINITY, ILK_PICKS,
} from "./markData";
import { useAchievements } from "./useAchievements";
import { getAchievementDisplay } from "./achievements";
import { MK } from "./markData";   // MK lives in markData now; this drags
                                    // Mark.js's whole achievement graph in for a
                                    // colour object otherwise.

const KINDS = ["track", "album", "book", "art", "misc"];

// ── atoms ────────────────────────────────────────────────────────────
const Label = ({ children, tint = MK.smokeP }) => (
  <div style={{
    fontFamily: MK.mono, fontSize: 9, letterSpacing: ".26em",
    textTransform: "uppercase", color: tint, marginBottom: 6,
  }}>{children}</div>
);

const field = {
  width: "100%", boxSizing: "border-box", background: MK.void,
  border: `1px solid ${MK.edge}`, color: MK.bone,
  fontFamily: MK.sans, fontSize: 14, padding: "8px 10px", outline: "none",
};

const btn = (tint, solid) => ({
  fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".22em",
  padding: "8px 14px", cursor: "pointer", textTransform: "uppercase",
  background: solid ? tint : "transparent",
  color: solid ? MK.void : tint,
  border: `1px solid ${tint}`,
  transition: "all .2s",
});

function Counter({ value, cap }) {
  const over = (value || "").length > cap * 0.85;
  return (
    <span style={{ fontFamily: MK.mono, fontSize: 9, color: over ? MK.gold : MK.smokeP, float: "right" }}>
      {(value || "").length}/{cap}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
export default function MarkEditor({ uid, onClose }) {
  const m = useMark(uid);
  const { earnedIds } = useAchievements(uid);
  const accent = accentOf(m.mark);

  const [tagline, setTagline] = useState("");
  const [status, setStatus]   = useState("");
  const [acc, setAcc]         = useState("gold");
  const [pins, setPins]       = useState([]);
  const [links, setLinks]     = useState([]);      // [[label, url], …]
  const [saving, setSaving]   = useState(false);
  const [note, setNote]       = useState("");
  const [pane, setPane]       = useState("card");  // card | deeds | store
  // ⭐ Ilk + Partner (2026-09-26). The tag is a draft sealed with the
  // rest of the card; the partner is set on its own button, like a pin.
  const [tagDraft, setTagDraft] = useState("?");
  const [partnerCode, setPartnerCode] = useState("");
  const ilk = ilkOf(m.mark);
  const tagErr = ilkTagError(tagDraft);
  const partnerUid = partnerOf(m.mark);
  const partnerMark = useMark(partnerUid);

  // Hydrate once the Mark arrives. Local state is the draft; nothing is
  // written until Seal is pressed, so a half-typed tagline never lands.
  useEffect(() => {
    if (m.loading) return;
    setTagline(m.mark.tagline || "");
    setStatus(m.mark.status || "");
    setAcc(m.mark.accent || "gold");
    setPins(m.mark.pinned || []);
    setLinks(Object.entries(m.mark.links || {}));
    setTagDraft(ilkOf(m.mark).tag);
  }, [m.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const earned = useMemo(() => new Set(earnedIds || []), [earnedIds]);

  const flash = (msg) => { setNote(msg); setTimeout(() => setNote(""), 2200); };

  const sealCard = async () => {
    setSaving(true);
    try {
      await saveMarkBasics(uid, { tagline, status, accent: acc });
      await saveLinks(uid, Object.fromEntries(links.filter(([l, u]) => l && safeUrl(u))));
      // The tag never holds the rest of the card hostage: an unfinished
      // tag is skipped (and said so), everything else still seals.
      if (tagErr) { flash(`SEALED — TAG KEPT: ${tagErr.toUpperCase()}`); return; }
      if (tagDraft.trim() !== ilk.tag) await saveIlkTag(uid, tagDraft);
      flash("MARK SEALED");
    } catch (e) {
      flash(e.message || "REFUSED");
    } finally { setSaving(false); }
  };

  // Picking from the chips: append while there is room, otherwise start
  // over with the chip — two picks, never a third.
  const pickChip = (c) => {
    const cur = Array.from(tagDraft.trim());
    setTagDraft(cur.length < ILK_PICKS && tagDraft.trim() !== "?" ? cur.concat(c).join("") : c);
  };

  const setPartnerByCode = async () => {
    try {
      if (!partnerCode.trim()) { flash("A CODE LOOKS LIKE MK-XXXX-XXXX"); return; }
      const p = await uidForCode(partnerCode);
      if (!p) { flash("NOBODY CARRIES THAT CODE"); return; }
      await savePartner(uid, p);
      setPartnerCode("");
      flash("PARTNER SET");
    } catch (e) { flash((e.message || "REFUSED").toUpperCase()); }
  };
  const clearPartner = async () => {
    try { await savePartner(uid, null); flash("PARTNER CLEARED"); }
    catch (e) { flash((e.message || "REFUSED").toUpperCase()); }
  };

  const togglePin = async (id) => {
    const next = pins.includes(id)
      ? pins.filter((p) => p !== id)
      : [...pins, id].slice(0, CAPS.pinned);
    setPins(next);
    try { await savePinned(uid, next); } catch (e) { flash(e.message); }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose?.()} style={{
      position: "fixed", inset: 0, zIndex: 9100,
      background: "rgba(3,3,6,.85)", backdropFilter: "blur(7px)",
      display: "grid", placeItems: "center", padding: 16,
      animation: "mkFade .2s both",
    }}>
      <div style={{
        width: "min(560px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column",
        background: `linear-gradient(158deg, ${MK.slab}, ${MK.obsidian} 55%, ${MK.void})`,
        border: `1px solid ${MK.edge}`,
        boxShadow: `0 26px 80px #000c, 0 0 44px ${accent.hex}18`,
        clipPath: "polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))",
        animation: "mkRise .3s cubic-bezier(.22,.61,.36,1) both",
      }}>
        {/* header */}
        <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${MK.edge}` }}>
          <Label tint={accent.hex}>EDIT YOUR MARK</Label>
          <div style={{ fontFamily: MK.display, fontWeight: 800, fontSize: 24, color: MK.bone, lineHeight: 1 }}>
            {m.name}
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 11 }}>
            {[["card", "CARD"], ["deeds", "PINS"], ["store", "STORE"]].map(([k, l]) => (
              <div key={k} onClick={() => setPane(k)} className="mk-tab" style={{
                fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".26em", cursor: "pointer",
                color: pane === k ? MK.bone : MK.smokeP,
                borderBottom: pane === k ? `1px solid ${accent.hex}` : "1px solid transparent",
                paddingBottom: 2,
              }}>{l}</div>
            ))}
          </div>
        </div>

        <div className="mk-scroll" style={{ padding: "16px 18px", overflowY: "auto", flex: 1 }}>
          {pane === "card" && (
            <>
              <Label>TAGLINE <Counter value={tagline} cap={CAPS.tagline} /></Label>
              <input style={field} value={tagline} maxLength={CAPS.tagline}
                placeholder="One line. Say who you are."
                onChange={(e) => setTagline(e.target.value)} />

              <div style={{ height: 16 }} />
              <Label>STATUS <Counter value={status} cap={CAPS.status} /></Label>
              <input style={field} value={status} maxLength={CAPS.status}
                placeholder="What you're doing right now"
                onChange={(e) => setStatus(e.target.value)} />

              <div style={{ height: 16 }} />
              <Label>ACCENT</Label>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {Object.entries(MARK_ACCENTS).map(([k, a]) => (
                  <div key={k} onClick={() => setAcc(k)} title={a.label} style={{
                    width: 34, height: 34, cursor: "pointer",
                    background: `linear-gradient(140deg, ${a.hi}, ${a.hex})`,
                    border: `2px solid ${acc === k ? MK.bone : "transparent"}`,
                    boxShadow: acc === k ? `0 0 16px ${a.hex}aa` : "none",
                    clipPath: "polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))",
                  }} />
                ))}
              </div>

              <div style={{ height: 18 }} />
              <Label>ILK — {ilk.name}. Shows under your name as {ilk.kind}-{tagErr ? "?" : tagDraft.trim()}</Label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{
                  fontFamily: MK.mono, fontSize: 14, color: MK.bone,
                  padding: "8px 10px", border: `1px solid ${MK.edge}`, background: MK.void,
                }}>{ilk.kind}-</div>
                <input style={{ ...field, flex: "0 0 84px", fontFamily: MK.mono, textAlign: "center" }}
                  value={tagDraft} placeholder="?"
                  onChange={(e) => setTagDraft(Array.from(e.target.value.trim()).slice(0, ILK_PICKS).join(""))} />
                <div onClick={() => setTagDraft("?")} style={{ ...btn(MK.smoke), padding: "8px 10px" }}>?</div>
              </div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 7 }}>
                {[...ILK_SYMBOLS.filter((c) => c !== "?"), ILK_INFINITY].map((c) => (
                  <div key={c} onClick={() => pickChip(c)} style={{
                    fontFamily: MK.mono, fontSize: 12, color: MK.ashP, cursor: "pointer",
                    border: `1px solid ${MK.edge}`, padding: "3px 8px",
                  }}>{c}</div>
                ))}
              </div>
              <div style={{ fontFamily: MK.sans, fontSize: 12, color: tagErr ? MK.gold : MK.smokeP, marginTop: 7, fontStyle: "italic" }}>
                {tagErr || "Two picks: symbols, ∞, or a number 0–50. Never forced — keep the ? if you like it."}
              </div>

              <div style={{ height: 18 }} />
              <Label>PARTNER — {partnerUid ? (partnerMark.loading ? "…" : partnerMark.name) : "none yet"}</Label>
              <div style={{ display: "flex", gap: 6 }}>
                <input style={{ ...field, flex: 1, fontFamily: MK.mono }} value={partnerCode}
                  placeholder="Their Mark code — MK-XXXX-XXXX"
                  onChange={(e) => setPartnerCode(e.target.value)} />
                <div onClick={setPartnerByCode} style={btn(accent.hex)}>SET</div>
                {partnerUid && <div onClick={clearPartner} style={btn(MK.smoke)}>CLEAR</div>}
              </div>
              <div style={{ fontFamily: MK.sans, fontSize: 12, color: MK.smokeP, marginTop: 7, fontStyle: "italic" }}>
                Your co-op partner in this unstable world. They set you back from their own Mark.
              </div>

              <div style={{ height: 18 }} />
              <Label>ELSEWHERE — up to {CAPS.links} links</Label>
              {links.map(([label, url], i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input style={{ ...field, flex: "0 0 34%" }} value={label} placeholder="label"
                    maxLength={CAPS.linkLabel}
                    onChange={(e) => setLinks(links.map((r, j) => j === i ? [e.target.value, r[1]] : r))} />
                  <input style={{ ...field, flex: 1 }} value={url} placeholder="https://…"
                    onChange={(e) => setLinks(links.map((r, j) => j === i ? [r[0], e.target.value] : r))} />
                  <div onClick={() => setLinks(links.filter((_, j) => j !== i))}
                    style={{ ...btn(MK.smoke), padding: "8px 10px" }}>✕</div>
                </div>
              ))}
              {links.length < CAPS.links && (
                <div onClick={() => setLinks([...links, ["", ""]])} style={btn(MK.smoke)}>+ LINK</div>
              )}
              <div style={{ fontFamily: MK.sans, fontSize: 12, color: MK.smokeP, marginTop: 8, fontStyle: "italic" }}>
                http(s) only — anything else is dropped on save, on purpose.
              </div>
            </>
          )}

          {pane === "deeds" && (
            <>
              <Label>PINNED DEEDS — {pins.length}/{CAPS.pinned} on your ID</Label>
              {earned.size === 0 && (
                <div style={{ fontFamily: MK.sans, fontSize: 13.5, color: MK.smokeP }}>
                  Nothing earned yet. Go do something worth pinning.
                </div>
              )}
              <div style={{ display: "grid", gap: 5 }}>
                {[...earned].map((id) => {
                  const d = getAchievementDisplay(id, true) || {};
                  const on = pins.includes(id);
                  const tint = d.tint || accent.hex;
                  return (
                    <div key={id} onClick={() => togglePin(id)} style={{
                      display: "flex", alignItems: "center", gap: 9, cursor: "pointer",
                      padding: "7px 9px", background: on ? `${tint}14` : MK.obsidian,
                      border: `1px solid ${on ? tint + "88" : MK.edge}`,
                      transition: "all .2s",
                    }}>
                      <span style={{ fontFamily: MK.display, fontSize: 14, color: tint }}>{d.glyph || d.icon || "◈"}</span>
                      <span style={{ fontFamily: MK.sans, fontSize: 14, color: on ? MK.bone : MK.ashP, flex: 1 }}>
                        {d.name || id}
                      </span>
                      <span style={{ fontFamily: MK.mono, fontSize: 9, letterSpacing: ".2em", color: on ? tint : MK.smokeP }}>
                        {on ? "PINNED" : "PIN"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {pane === "store" && <StorePane uid={uid} m={m} accent={accent} flash={flash} />}
        </div>

        {/* footer */}
        <div style={{
          padding: "11px 18px", borderTop: `1px solid ${MK.edge}`,
          display: "flex", gap: 9, alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".2em", color: note ? MK.verd : MK.smoke }}>
            {note || m.code}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div onClick={onClose} style={btn(MK.smoke)}>CLOSE</div>
            {pane !== "store" && (
              <div onClick={saving ? undefined : sealCard} style={btn(accent.hex, true)}>
                {saving ? "SEALING…" : "SEAL"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STORE PANE — listings. A listing is a shop window, never a payment.
// ════════════════════════════════════════════════════════════════════
function StorePane({ uid, m, accent, flash }) {
  const [draft, setDraft] = useState({ kind: "track", title: "", blurb: "", priceDC: 0, cover: "", href: "" });
  const items = m.storeItems || [];

  const add = async () => {
    if (!draft.title.trim()) return flash("NEEDS A TITLE");
    try {
      await upsertListing(uid, draft);
      setDraft({ kind: draft.kind, title: "", blurb: "", priceDC: 0, cover: "", href: "" });
      flash("LISTED");
    } catch (e) { flash(e.message || "REFUSED"); }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Label tint={m.mark.store?.open ? MK.verd : MK.smoke}>
          {m.mark.store?.open ? "SHOP OPEN" : "SHOP CLOSED"}
        </Label>
        <div onClick={() => setStoreOpen(uid, !m.mark.store?.open).catch((e) => flash(e.message))}
          style={btn(m.mark.store?.open ? MK.smokeP : MK.verd)}>
          {m.mark.store?.open ? "CLOSE SHOP" : "OPEN SHOP"}
        </div>
      </div>

      {items.map((it) => (
        <div key={it.id} style={{
          display: "flex", alignItems: "center", gap: 9, padding: 8, marginBottom: 6,
          background: MK.obsidian, border: `1px solid ${MK.edge}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MK.sans, fontSize: 14, color: MK.bone, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {it.title}
            </div>
            <div style={{ fontFamily: MK.mono, fontSize: 8.5, letterSpacing: ".2em", color: MK.smokeP, textTransform: "uppercase" }}>
              {it.kind} · {it.priceDC ? `◆ ${fmtNum(it.priceDC)}` : "FREE"}
            </div>
          </div>
          <div onClick={() => removeListing(uid, it.id).catch((e) => flash(e.message))}
            style={{ ...btn(MK.smoke), padding: "6px 9px" }}>✕</div>
        </div>
      ))}

      <div style={{ height: 12 }} />
      <Label tint={accent.hex}>NEW LISTING</Label>
      <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
        {KINDS.map((k) => (
          <div key={k} onClick={() => setDraft({ ...draft, kind: k })} style={{
            ...btn(draft.kind === k ? accent.hex : MK.smokeP, draft.kind === k),
            padding: "6px 10px",
          }}>{k}</div>
        ))}
      </div>
      <input style={field} value={draft.title} maxLength={CAPS.itemTitle} placeholder="Title"
        onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
      <div style={{ height: 6 }} />
      <input style={field} value={draft.blurb} maxLength={CAPS.itemBlurb} placeholder="One-line blurb"
        onChange={(e) => setDraft({ ...draft, blurb: e.target.value })} />
      <div style={{ height: 6 }} />
      <div style={{ display: "flex", gap: 6 }}>
        <input style={{ ...field, flex: "0 0 32%" }} type="number" min={0} value={draft.priceDC}
          placeholder="◆ price (0 = free)"
          onChange={(e) => setDraft({ ...draft, priceDC: e.target.value })} />
        <input style={{ ...field, flex: 1 }} value={draft.cover} placeholder="cover image url (CDN)"
          onChange={(e) => setDraft({ ...draft, cover: e.target.value })} />
      </div>
      <div style={{ height: 6 }} />
      <input style={field} value={draft.href} placeholder="link (where it lives)"
        onChange={(e) => setDraft({ ...draft, href: e.target.value })} />
      <div style={{ height: 10 }} />
      <div onClick={add} style={btn(accent.hex, true)}>ADD LISTING</div>
      <div style={{ fontFamily: MK.sans, fontSize: 12, color: MK.smokeP, marginTop: 10, fontStyle: "italic", lineHeight: 1.5 }}>
        A listing is a shop window. It shows a price; it never takes one. When
        vending lands, the price is re-read and settled server-side — never
        from whatever a buyer's client happened to be holding.
      </div>
    </>
  );
}
